import React, { HTMLAttributeAnchorTarget, JSXElementConstructor, ReactElement, useEffect, useState } from "react"
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';
import { useIntlWrapperContext } from "../../Helpers/IntlWrapperProvider";
import useFetchWrapper, { HttpResponse } from "../../Helpers/useFetchWrapper";
import useObjectHelper from '../../Helpers/useObjectHelper';
import { researchFields } from '../../Helpers/StaticData';
import useScoreHelper, { Score } from '../../Helpers/useScoreHelper';

import { Action, FormPopupField } from "../../components/Popup/FormPopup.model";
import {
	Rating,
	UserProfileModel,
	UserProfileResponse,
	UserProfileUpdateModel,
} from "./BackofficeUserProfile.model";

export const pageSize = 10;

const useUserProfile = (props: { id: string }) => {
	const [ averageScore, setAverageScore ] = useState<Score | undefined>();
	const [ loading, setLoading ] = useState(true);
	const [ profile, setProfile ] = useState<UserProfileModel>();
	const [ profileResponse, setProfileResponse ] = useState<UserProfileResponse>();
	const [ ratings, setRatings ] = useState<Rating[]>();
	const [ ratingsPagination, setRatingsPagination ] = useState({ page: 1, pageSize , total: 0});
	const [ showDeletePopup, setShowDeletePopup ] = useState(false);
	const [ showEditForm, setShowEditForm ] = useState(false);

	const { id } = props;
	
	const { get, put } = useFetchWrapper();
	const intl = useIntl();
	const { arrToOptionList, objToOptionList } = useObjectHelper();
	const { getScoreByAverageRating } = useScoreHelper();
  const history = useHistory();
	const { isAuthorized } = useAuthorizationDataContext();

	const {
		getI18nCountryName,
		getI18nLanguageName,
		i18nCountries,
		i18nLanguages
	} = useIntlWrapperContext();

	const editFormFields: FormPopupField[] = [
		{
			id: "firstName",
			layout: "w50",
			rules: { required: true },
			type: "text",
		},
		{
			id: "lastName",
			layout: "w50",
			rules: { required: true },
			type: "text",
		},
		{
			id: "location",
			layout: "w50",
			options: { optionList: objToOptionList(i18nCountries), type: 'codeBased',  },
			type: "select",
		},
		{
			id: "languages",
			layout: "w50",
			options: { optionList: objToOptionList(i18nLanguages), type: 'codeBased' },
			type: "selectMultiple",
		},
		 {
			id: "researchAreas",
			layout: "w100",
			options: { optionList: arrToOptionList(researchFields) },
			type: "selectMultiple",
		},
		{
			id: "introduction",
			layout: "w100",
			type: "textArea",
		},
	];

	const editFormActions: Action[] = [{ onClick: () => { setShowEditForm(false) }, title: 'cancel', type: 'default' }, {
		isSubmitButton: true,
		title: 'save',
		type: 'primary'
	}];

	useEffect(() => {
		getUserProfile();
	}, []);
	
	useEffect(() => {
		profile?.role === 'Mentor' && getRatings();
	}, [ profile ]);

	const deleteUser = async (id: string) => {
		await put(`/user/delete/${id}`, {});

		history.push('/users');
	}

	const getUserProfile = async () => {
		!loading && setLoading(true);

		const response: HttpResponse<UserProfileResponse> = await get(`/user/profile/${id}`);
		if (response.parsedBody) {
			const {
				acceptedAt,
				currentPosition,
				education,
				email,
				firstName,
				imageId,
				introduction,
				languages,
				lastName,
				location,
				mentoringScopes,
				researchAreas,
				role,
				workExperience,
			} = response.parsedBody;

			const user: UserProfileModel = {
				acceptedAt,
				currentPosition: currentPosition ? intl.formatMessage({ id: `currentPosition.${currentPosition}`}) : '',
				education,
				email,
				imageId,
				introduction,
				languages,
				location: location ? getI18nCountryName(location) : '',
				mentoringScopes,
				name: `${firstName} ${lastName} `,
				researchAreas,
				role: role,
				workExperience: workExperience,
			};
			setProfile(user);
			setProfileResponse(response.parsedBody)
		}
		setLoading(false);
	}
	
	const getRatings = async () => {
		const response: HttpResponse<{averageScore: number, ratings: Rating[], count: number}> = await get(`/review/rating/${id}?page=${ratingsPagination.page}&size=${ratingsPagination.pageSize}`);
		if (response.parsedBody) {
			const { averageScore, count, ratings } = response.parsedBody;

			setRatings(ratings);
			setRatingsPagination({ ...ratingsPagination, total: count });
			setAverageScore(getScoreByAverageRating(averageScore));
		}
	}

	const onChangeRatingsPagination = (page: number) => {
		setRatingsPagination({ ...ratingsPagination, page });
	}

	const editUserProfile = async (formData: UserProfileUpdateModel) => {
		if (profileResponse) {
			const { conference, title, pronouns, workExperience, education }: UserProfileResponse = profileResponse;
			await put(`/user/profile/${id}`, { ...formData, conference: conference?.id, education, pronouns, title, workExperience });
			getUserProfile();
		}
	}

	const toggleDeletePopup = () => {
		setShowDeletePopup((currentValue: boolean) => !currentValue);
	}

	const toggleEditForm = () => {
		setShowEditForm((currentValue: boolean) => !currentValue);
	}

	return {
		deleteUser,
		editUserProfile,
		getI18nLanguageName,
		isAuthorized,
		onChangeRatingsPagination,
		state: {
			averageScore,
			editFormActions,
			editFormFields,
			loading,
			profile,
			profileResponse,
			ratings,
			ratingsPagination,
			showDeletePopup,
			showEditForm,
		},
		toggleDeletePopup,
		toggleEditForm,
	}

}
export default useUserProfile;